import { ValidationErrors, SignupData } from '../types';

// US phone number regex patterns
const US_PHONE_REGEX = /^(\+1|1)?[-.\s]?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
const FORMATTED_PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;

/**
 * Format phone number to US standard format
 * Example: "1234567890" -> "(123) 456-7890"
 */
export const formatPhoneNumber = (phone: string): string => {
  if (typeof phone !== 'string') {
    return '';
  }
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Remove leading 1 if present
  const cleanDigits = digits.startsWith('1') ? digits.substring(1) : digits;
  
  // Limit to 10 digits for US phone numbers
  const limitedDigits = cleanDigits.slice(0, 10);
  
  // Format as (XXX) XXX-XXXX
  if (limitedDigits.length === 10) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  } else if (limitedDigits.length >= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  } else if (limitedDigits.length >= 3) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  }
  
  return limitedDigits;
};

/**
 * Validate US phone number
 * Supports both unformatted and formatted numbers like (123) 456-7890
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  if (typeof phone !== 'string') {
    return false;
  }
  
  // Check if it's in the formatted format (123) 456-7890
  if (FORMATTED_PHONE_REGEX.test(phone)) {
    return true;
  }
  
  // Check if it's in unformatted format
  return US_PHONE_REGEX.test(phone);
};

/**
 * Validate full name
 */
export const isValidFullName = (name: string): boolean => {
  return typeof name === 'string' && name.trim().length >= 2;
};

/**
 * Validate OTP code
 */
export const isValidOTP = (otp: string): boolean => {
  return typeof otp === 'string' && /^\d{6}$/.test(otp);
};

/**
 * Comprehensive validation for signup form
 */
export const validateSignupData = (data: SignupData): ValidationErrors => {
  const errors: ValidationErrors = {};
  
  if (!isValidFullName(data.fullName)) {
    errors.fullName = 'Please enter your full name (at least 2 characters)';
  }
  
  if (!isValidPhoneNumber(data.phoneNumber)) {
    errors.phoneNumber = 'Please enter a valid US phone number';
  }
  
  return errors;
};

/**
 * Validate OTP input
 */
export const validateOTP = (otp: string): ValidationErrors => {
  const errors: ValidationErrors = {};
  
  if (!isValidOTP(otp)) {
    errors.otp = 'Please enter a valid 6-digit code';
  }
  
  return errors;
};

/**
 * Sanitize input to prevent XSS and other security issues
 */
export const sanitizeInput = (input: string): string => {
  return input.replace(/[<>]/g, '');
};
